import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { I18nService } from '../../core/i18n.service';

// Add / edit an employee. Deliberately small: name, email, employee number and
// group. Credit balance and subsidy are commercial terms and are not editable
// from the portal, and passwords are never set here — the employee gets an
// email and chooses their own.
@Component({
  selector: 'portal-employee-dialog',
  template: `
    <h2 mat-dialog-title>{{ (isAdd ? 'employeeDialog.addTitle' : 'employeeDialog.editTitle') | translate }}</h2>

    <mat-dialog-content>
      <mat-form-field appearance="outline" class="full">
        <mat-label>{{ 'employeeDialog.fullName' | translate }}</mat-label>
        <input matInput [(ngModel)]="form.name" autocomplete="off">
      </mat-form-field>

      <mat-form-field appearance="outline" class="full" *ngIf="isAdd">
        <mat-label>{{ 'employeeDialog.workEmail' | translate }}</mat-label>
        <input matInput type="email" [(ngModel)]="form.email" autocomplete="off">
        <mat-hint>{{ 'employeeDialog.workEmailHint' | translate }}</mat-hint>
      </mat-form-field>

      <div class="readonly-row" *ngIf="!isAdd">
        <span class="readonly-label">{{ 'employeeDialog.email' | translate }}</span>
        <span class="readonly-value">{{ data.employee.email || '—' }}</span>
      </div>

      <mat-form-field appearance="outline" class="full">
        <mat-label>{{ 'employeeDialog.employeeNumber' | translate }}</mat-label>
        <input matInput [(ngModel)]="form.employeeCode" autocomplete="off">
      </mat-form-field>

      <mat-form-field appearance="outline" class="full">
        <mat-label>{{ 'employeeDialog.group' | translate }}</mat-label>
        <mat-select [(ngModel)]="form.groupDocId">
          <mat-option *ngFor="let g of data.groups" [value]="g.id">
            {{ g.name }}
            <span class="group-hint" *ngIf="g.type === 'subsidy'"> — {{ 'employeeDialog.subsidised' | translate:{pct: g.subsidy} }}</span>
            <span class="group-hint" *ngIf="g.type === 'credit'"> — {{ 'employeeDialog.monthlyCredit' | translate }}</span>
          </mat-option>
        </mat-select>
        <mat-hint>{{ 'employeeDialog.groupHint' | translate }}</mat-hint>
      </mat-form-field>

      <label class="invite-row" *ngIf="isAdd">
        <input type="checkbox" [(ngModel)]="form.sendInvite">
        <span>{{ 'employeeDialog.sendInvite' | translate }}</span>
      </label>

      <p class="dialog-error" *ngIf="error">{{ error }}</p>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="close()">{{ 'employeeDialog.cancel' | translate }}</button>
      <button mat-flat-button color="primary" (click)="save()">{{ (isAdd ? 'employeeDialog.addTitle' : 'employeeDialog.save') | translate }}</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .full { width: 100%; }
    .group-hint { color: #7b8794; font-size: 12px; }
    .invite-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #46617a; margin: 4px 0 8px; cursor: pointer; }
    .readonly-row { display: flex; gap: 10px; align-items: baseline; margin: 0 0 18px; }
    .readonly-label { font-size: 12px; color: #8593a1; text-transform: uppercase; letter-spacing: 0.4px; }
    .readonly-value { font-size: 14px; }
    .dialog-error { color: #b3271e; font-size: 13px; margin: 0; }
  `]
})
export class EmployeeDialogComponent {

  isAdd: boolean;
  error = '';
  form: any;

  constructor(
    private ref: MatDialogRef<EmployeeDialogComponent>,
    private i18n: I18nService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isAdd = data.mode === 'add';
    const e = data.employee || {};
    this.form = {
      name: e.name || '',
      email: '',
      employeeCode: e.employeeCode || '',
      groupDocId: e.groupDocId || (data.groups.length === 1 ? data.groups[0].id : ''),
      sendInvite: true
    };
  }

  save() {
    this.error = '';
    if (!this.form.name.trim()) { this.error = this.i18n.t('employeeDialog.errorNameRequired'); return; }
    if (!this.form.groupDocId) { this.error = this.i18n.t('employeeDialog.errorGroupRequired'); return; }

    if (this.isAdd) {
      const email = this.form.email.trim().toLowerCase();
      if (!email || !email.includes('@')) { this.error = this.i18n.t('employeeDialog.errorEmailRequired'); return; }
      this.ref.close({
        name: this.form.name.trim(),
        email,
        employeeCode: this.form.employeeCode.trim(),
        groupDocId: this.form.groupDocId,
        sendInvite: this.form.sendInvite !== false
      });
      return;
    }

    // Edit sends only what changed, so an untouched field can never overwrite a
    // value that was updated elsewhere in the meantime.
    const e = this.data.employee;
    const patch: any = {};
    if (this.form.name.trim() !== e.name) { patch.name = this.form.name.trim(); }
    if (this.form.employeeCode.trim() !== (e.employeeCode || '')) { patch.employeeCode = this.form.employeeCode.trim(); }
    if (this.form.groupDocId !== e.groupDocId) { patch.groupDocId = this.form.groupDocId; }

    if (!Object.keys(patch).length) { this.ref.close(null); return; }
    this.ref.close(patch);
  }

  close() { this.ref.close(null); }
}
